import React, { useEffect, useState, useMemo } from 'react';
import { v2 } from '@fieldnation/platform-components';
import {
  Part, PartUsage, USAGE_CODES, NOTE_PLACEHOLDERS,
} from './types.d';
import styles from './styles.scss';
import TrackingField from '../ShipmentForm/components/TrackingField';
import {
  getUsageHeader,
  RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
  RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
} from './utils';
import { getCarrierFromTrackingNumber } from '../ShipmentForm/utils';

const { Select, Checkbox, TextArea } = v2;

interface Props {
  partData: Part;
  usageData: PartUsage;
  returnTrackingRequired?: boolean;
  setReturnTrackingRequired?: React.Dispatch<React.SetStateAction<boolean>>
  setUsageData: React.Dispatch<React.SetStateAction<PartUsage>>;
  usageCodes: string[];
  onStateChange?: ((id: number, type: 'part' | 'usage', data: PartUsage) => void) | null;
  fromWorkOrderCreate?: boolean;
  isProvider?: boolean;
}

const Usage = ({
  partData,
  usageData,
  returnTrackingRequired,
  setReturnTrackingRequired,
  setUsageData,
  usageCodes,
  onStateChange,
  fromWorkOrderCreate,
  isProvider = false,
}: Props): JSX.Element => {
  const [showOriginalPartLabel, setShowOriginalPartLabel] = useState(false);
  const [showReturnTrackingInput, setShowReturnTrackingInput] = useState(false);
  const [showReturnInstruction, setShowReturnInstruction] = useState(false);
  const [showCustomerRetained, setShowCustomerRetained] = useState(false);
  const [returnShipmentTrackingLink, setReturnShipmentTrackingLink] = useState('');

  const returnInstructionLabel = useMemo(() => {
    const options = [
      ...RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
      ...RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
    ];
    return options.find((opt) => opt.value === partData?.returnInstruction)?.label || partData?.returnInstruction;
  }, [isProvider, partData?.returnInstruction]);

  const onUsageCodeSelect = (selectedUsageCode) => {
    setUsageData({
      ...usageData,
      usageCode: selectedUsageCode,
      ...(selectedUsageCode !== USAGE_CODES.USED
        ? { customerRetained: '' }
        : {}),
    });
  };

  const onRetainedByCustomerChange = (selectedValues) => {
    const isChecked = selectedValues.includes('1');

    // return tracking is not required if customer retained is checked
    if (isChecked && setReturnTrackingRequired) {
      setReturnTrackingRequired(false);
    }

    setUsageData({
      ...usageData,
      customerRetained: isChecked ? '1' : '0',
    });
  };

  // Get carrier tracking link on first load
  useEffect(() => {
    if (usageData?.returnTracking) {
      const { trackingLink } = getCarrierFromTrackingNumber(usageData.returnTracking);
      setReturnShipmentTrackingLink(trackingLink);
    }
  }, []);

  // Need when adding part from work order create
  useEffect(() => {
    if (onStateChange && partData?.id) {
      onStateChange(partData.id, 'usage', usageData);
    }
  }, [usageData]);

  useEffect(() => {
    // Do not show return intruction when usage code is Not Used, Does Not Work, Damaged In Transit or Wrong Part and returnable is No
    // Or when usage code is Not Received
    if (([
      USAGE_CODES.NOT_USED,
      USAGE_CODES.DOES_NOT_WORK,
      USAGE_CODES.DAMAGED_IN_TRANSIT,
      USAGE_CODES.WRONG_PART,
    ].includes(usageData?.usageCode as USAGE_CODES) && partData?.returnable === 'No') ||
      usageData?.usageCode === USAGE_CODES.NOT_RECEIVED || !usageData?.usageCode
    ) {
      setShowReturnInstruction(false);
    } else {
      setShowReturnInstruction(true);
    }

    if (usageData?.usageCode === USAGE_CODES.USED) {
      setShowOriginalPartLabel(true);
    } else {
      setShowOriginalPartLabel(false);
    }

    // Do not show return tracking label when usage code is `Used` and returnable is `No`
    // Or when Usage code is `Not received`
    if (
      (usageData?.usageCode === USAGE_CODES.USED && partData?.returnable === 'No') ||
      usageData?.usageCode === USAGE_CODES.NOT_RECEIVED
    ) {
      setShowReturnTrackingInput(false);
      if (!fromWorkOrderCreate && setReturnTrackingRequired) {
        setReturnTrackingRequired(false);
      }
    } else {
      if (usageData?.usageCode) {
        setShowReturnTrackingInput(true);
      }
      if (!fromWorkOrderCreate && setReturnTrackingRequired) {
        // Return tracking is not required if usage code is `Not used`, `Does not work`, `Damaged in transit`, `Wrong part` or `Not received` and returnable is No
        // Or when usage code is `Not received`
        if (([
          USAGE_CODES.NOT_USED,
          USAGE_CODES.DOES_NOT_WORK,
          USAGE_CODES.DAMAGED_IN_TRANSIT,
          USAGE_CODES.WRONG_PART,
        ].includes(usageData?.usageCode as USAGE_CODES) && partData?.returnable === 'No') ||
          usageData?.usageCode === USAGE_CODES.NOT_RECEIVED) {
          setReturnTrackingRequired(false);
        } else if (usageData?.customerRetained !== '1') {
          setReturnTrackingRequired(true);
        }
      }
    }

    // Show customer retained checkbox when usage code is Used and returnable is Yes
    if (usageData?.usageCode === USAGE_CODES.USED && partData?.returnable === 'Yes') {
      setShowCustomerRetained(true);
    } else {
      setShowCustomerRetained(false);
    }
  }, [usageData]);

  return (
    <>
      {/* New part label */}
      <div className={styles['usage-header']}>
        {getUsageHeader(partData.externalId, partData.externalNumber, partData.description)}
      </div>
      <p className={styles['usage-subtitle']}>New part</p>
      <Select
        label="Usage code"
        options={usageCodes.map((code) => ({ label: code, value: code }))}
        onChange={(option) => onUsageCodeSelect(option.value)}
        value={usageData.usageCode}
      />

      {/* Defective part label */}
      {showOriginalPartLabel && (
        <div className={styles['original-part-subtitle']}>Defective part</div>
      )}

      {/* Return instructions */}
      {showReturnInstruction && (
        <div className={styles['item-spacing']}>
          <p>
            {returnInstructionLabel}
          </p>
        </div>
      )}

      {/* Return tracking input */}
      {showReturnTrackingInput && (
        <div className={styles['item-spacing-md']}>
          <TrackingField
            inputLabel="Return tracking number"
            value={{
              name: usageData.returnCarrier || '',
              tracking: usageData.returnTracking ?? '',
              other_name: usageData.returnCarrierOtherName || '',
              tracking_link: returnShipmentTrackingLink || '',
            }}
            onChange={(carrierInfo) => {
              setUsageData({
                ...usageData,
                returnCarrier: carrierInfo?.name || '',
                returnTracking: carrierInfo.tracking,
                returnCarrierOtherName: carrierInfo?.other_name ?? '',
              });
              setReturnShipmentTrackingLink(carrierInfo.tracking_link || '');
            }}
            disabled={usageData.customerRetained === '1'}
            optional={!returnTrackingRequired}
            errors={{ name: false, tracking: false }}
          />
        </div>
      )}

      {/* Customer retained input */}
      {showCustomerRetained && (
        <div className={styles['item-spacing-md']}>
          <Checkbox
            options={[{ label: 'Unable to return - the customer kept on site', value: '1' }]}
            onChange={onRetainedByCustomerChange}
            value={usageData.customerRetained === '1' ? ['1'] : []}
          />
        </div>
      )}

      {usageData.usageCode && (
        <>
          <div className={styles['item-spacing-md']}>
            <hr className={styles.separator} />
          </div>
          <div className={styles['item-spacing-md']}>
            <TextArea
              id="notes"
              label="Notes"
              placeholder={NOTE_PLACEHOLDERS[usageData.usageCode]}
              value={usageData.note || ''}
              onChange={(e) => setUsageData({
                ...usageData,
                note: e.target.value,
              })}
              optional
            />
          </div>
        </>
      )}
    </>
  );
};

Usage.defaultProps = {
  onStateChange: null,
  returnTrackingRequired: null,
  setReturnTrackingRequired: null,
  fromWorkOrderCreate: false,
  isProvider: false,
};

export default Usage;
